// import node module libraries
import { Fragment, useContext, useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import {
	ListGroup,
	Accordion,
	Card,
	Image,
	Badge,
	useAccordionButton,
	AccordionContext,
	OverlayTrigger,
	Tooltip
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getRoleId } from 'helper/utils';
import useRole from 'hooks/useRole';

import DotBadge from 'components/elements/bootstrap/DotBadge';

// import simple bar scrolling used for notification item scrolling
import SimpleBar from 'simplebar-react';
import 'simplebar/dist/simplebar.min.css';

// import media files
import InverseLogo from 'assets/images/brand/logo/logo-inverse.svg';

// import routes file
import { DashboardMenuUser } from 'routes/dashboard/DashboardRoutesUSER';
import { DashboardMenuKeyuser } from 'routes/dashboard/DashboardRoutesKEYUSER';
import { DashboardMenuSysadmin } from 'routes/dashboard/DashboardRoutesSYSADMIN';
import { Dot } from 'react-bootstrap-icons';

const NavbarVertical = (props) => {
	const { t, i18n } = useTranslation();
	const location = useLocation();

	// Get Role ID and Role
	const roleId = getRoleId();
	const { role, loading } = useRole(roleId);

	// State for subscriptions to trigger re-renders when localStorage changes
	const [subscriptions, setSubscriptions] = useState(() => {
		return JSON.parse(localStorage.getItem('_tenant_subscriptions')) || [];
	});

	// Listen for localStorage changes
	useEffect(() => {
		const handleStorageChange = () => {
			const updatedSubscriptions = JSON.parse(localStorage.getItem('_tenant_subscriptions')) || [];
			setSubscriptions(updatedSubscriptions);
		};

		// Listen for storage events (from other tabs)
		window.addEventListener('storage', handleStorageChange);

		// Custom event for same-tab localStorage changes
		window.addEventListener('localStorageUpdate', handleStorageChange);

		return () => {
			window.removeEventListener('storage', handleStorageChange);
			window.removeEventListener('localStorageUpdate', handleStorageChange);
		};
	}, []);

	const CustomToggle = ({ children, eventKey, icon }) => {
		const { activeEventKey } = useContext(AccordionContext);
		const decoratedOnClick = useAccordionButton(eventKey, () =>
			console.log('Event Key : ' + eventKey)
		);
		const isCurrentEventKey = activeEventKey === eventKey;
		return (
			<li className="nav-item">
				<Link
					className="nav-link "
					onClick={decoratedOnClick}
					to="#!"
					data-bs-toggle="collapse"
					data-bs-target="#navDashboard"
					aria-expanded={isCurrentEventKey ? true : false}
					aria-controls="navDashboard"
				>
					{icon ? <i className={`nav-icon fe fe-${icon} me-2`}></i> : ''}{' '}
					{children}
				</Link>
			</li>
		);
	};

	const generateLink = (item) => {
		return (
			<Link
				className={`nav-link ${
					location.pathname === item.link ? 'active' : ''
				}`}
				to={item.link}
				onClick={(e) =>
					isMobile ? props.onClick(!props.showMenu) : props.showMenu
				}
			>
				{item.name}
				{''}
				{item.badge ? (
					<Badge
						className="ms-1"
						bg={item.badgecolor ? item.badgecolor : 'primary'}
					>
						{item.badge}
					</Badge>
				) : (
					''
				)}
			</Link>
		);
	};

	// Helper to get current plan info
	const getCurrentSubscription = () => {
		if (!subscriptions || subscriptions.length === 0) return null;
		const subscription = subscriptions[0];
		const plan = subscription.plan || (subscription.items?.[0]?.plan);
		const price = subscription.items?.[0]?.price;
		const product = subscription.product;
		const isTrial = subscription.status === 'trialing' && subscription.trial_end;
		return {
			id: plan.id,
			name: product?.name || plan?.id || t('Unknown Plan'),
			amount: (price?.unit_amount ?? plan?.amount ?? 0) / 100,
			currency: (price?.currency ?? plan?.currency ?? 'eur').toUpperCase(),
			interval: price?.recurring?.interval || plan?.interval || '',
			status: subscription.status,
			canceled: {
				cancel_at_period_end: subscription.cancel_at_period_end || false,
				cancel_at: subscription.cancel_at || null,
				canceled_at: subscription.canceled_at || null,
			},
			nextBilling: subscription.items?.[0]?.current_period_end
				? new Date(subscription.items.current_period_end * 1000).toLocaleDateString()
				: null,
			trial: isTrial
				? {
					end: subscription.trial_end || null,
					start: subscription.trial_start || null
				}
				: null,
			marketing_features: product?.marketing_features || [],
		};
	};

	const currentSubscription = useMemo(() => {
		return getCurrentSubscription();
	}, [subscriptions]);

	// Helper function to calculate remaining trial days
	const calculateDaysLeft = (endTimestamp) => {
		const now = new Date();
		const end = new Date(endTimestamp * 1000);
		const diffTime = end - now;
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	// Helper function to get status color
	const getStatusColor = (status) => {
		switch (status) {
			case 'active':
				return 'success';
			case 'trialing':
				return 'warning';
			case 'canceled':
				return 'info';
			case 'incomplete_expired':
				return 'danger';
			default:
				return 'info';
		}
	};

	// Check if the screen is mobile
	const isMobile = useMediaQuery({ maxWidth: 767 });
	
	return (
		<Fragment>
			<SimpleBar style={{ maxHeight: '100vh' }}>
				<div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
					<div className="nav-scroller">
						<Link className="navbar-brand" to="/dashboard/overview">
							<Image src={InverseLogo} alt="" />
						</Link>
					</div>
					{/* Dashboard Menu */}
					<Accordion
						defaultActiveKey="0"
						as="ul"
						className="navbar-nav flex-column"
						style={{ flexGrow: 1 }}
									>
					{(role === 'USER'
						? DashboardMenuUser
						: role === 'KEYUSER'
							? DashboardMenuKeyuser
							: role === 'SYSADMIN'
							? DashboardMenuSysadmin
							: DashboardMenuUser // Default value if role is not USER, KEYUSER, or SYSADMIN
					).map(function (menu, index) {
							if (menu.grouptitle) {
								return (
									<Card bsPrefix="nav-item" key={index}>
										{/* group title item */}
										<div className="navbar-heading">{menu.title}</div>
										{/* end of group title item */}
									</Card>
								);
							} else {
								if (menu.children) {
									return (
										<Fragment key={index}>
											{/* main menu / menu level 1 / root items */}
											<CustomToggle eventKey={menu.id} icon={menu.icon}>
												{menu.title}
												{menu.badge ? (
													<Badge
														className="ms-1"
														bg={menu.badgecolor ? menu.badgecolor : 'primary'}
													>
														{menu.badge}
													</Badge>
												) : (
													''
												)}
											</CustomToggle>
											<Accordion.Collapse
												eventKey={menu.id}
												as="li"
												bsPrefix="nav-item"
											>
												<Accordion className="navbar-nav flex-column" as="ul">
													<ListGroup
														as="ul"
														bsPrefix=""
														className="nav flex-column"
													>
														{menu.children.map(function (
															menuItem,
															menuItemIndex
														) {
															if (menuItem.children) {
																return (
																	<Fragment key={menuItemIndex}>
																		{/* second level with children */}
																		<CustomToggle eventKey={menuItem.id}>
																			{menuItem.title}
																			{menuItem.badge ? (
																				<Badge
																					className="ms-1"
																					bg={
																						menuItem.badgecolor
																							? menuItem.badgecolor
																							: 'primary'
																					}
																				>
																					{menuItem.badge}
																				</Badge>
																			) : (
																				''
																			)}
																		</CustomToggle>
																		<Accordion.Collapse
																			eventKey={menuItem.id}
																			bsPrefix="nav-item"
																			as="li"
																		>
																			<Accordion
																				className="navbar-nav flex-column"
																				as="ul"
																			>
																				<ListGroup
																					as="ul"
																					bsPrefix=""
																					className="nav flex-column"
																				>
																					{/* third level menu started  */}
																					{menuItem.children.map(function (
																						subMenuItem,
																						subMenuItemIndex
																					) {
																						return subMenuItem.children ? (
																							<Fragment key={subMenuItemIndex}>
																								<CustomToggle
																									eventKey={subMenuItem.id}
																								>
																									{subMenuItem.title}
																									{subMenuItem.badge ? (
																										<Badge
																											className="ms-1"
																											bg={
																												subMenuItem.badgecolor
																													? subMenuItem.badgecolor
																													: 'primary'
																											}
																										>
																											{subMenuItem.badge}
																										</Badge>
																									) : (
																										''
																									)}
																								</CustomToggle>
																								<Accordion.Collapse
																									eventKey={subMenuItem.id}
																									bsPrefix="nav-item"
																									as="li"
																								>
																									<ListGroup
																										as="ul"
																										bsPrefix=""
																										className="nav flex-column"
																									>
																										{subMenuItem.children.map(
																											function (
																												thirdLevelItem,
																												thirdLevelItemIndex
																											) {
																												return (
																													<ListGroup.Item
																														key={
																															thirdLevelItemIndex
																														}
																														as="li"
																														bsPrefix="nav-item"
																													>
																														{/* third level with children */}
																														{generateLink(
																															thirdLevelItem
																														)}
																													</ListGroup.Item>
																												);
																											}
																										)}
																									</ListGroup>
																								</Accordion.Collapse>
																							</Fragment>
																						) : (
																							<ListGroup.Item
																								key={subMenuItemIndex}
																								as="li"
																								bsPrefix="nav-item"
																							>
																								{/* third level without children */}
																								{generateLink(subMenuItem)}
																							</ListGroup.Item>
																						);
																					})}
																					{/* end of third level menu  */}
																				</ListGroup>
																			</Accordion>
																		</Accordion.Collapse>
																		{/* end of second level with children */}
																	</Fragment>
																);
															} else {
																return (
																	<ListGroup.Item
																		as="li"
																		bsPrefix="nav-item"
																		key={menuItemIndex}
																	>
																		{/* second level without children */}
																		{generateLink(menuItem)}
																		{/* end of second level without children  */}
																	</ListGroup.Item>
																);
															}
														})}
													</ListGroup>
												</Accordion>
											</Accordion.Collapse>
											{/* end of main menu / menu level 1 / root items */}
										</Fragment>
									);
								} else {
									return (
										<Card bsPrefix="nav-item" key={index}>
											{/* menu item without any childern items like Help Center, Documentation and Changelog items*/}
											<Link
												to={menu.link}
												className={`nav-link ${
													location.pathname === menu.link ? 'active' : ''
												}`}
											>
												{typeof menu.icon === 'string' ? (
													<i className={`nav-icon fe fe-${menu.icon} me-2`}></i>
												) : (
													menu.icon
												)}
												{menu.title}
												{menu.badge ? (
													<Badge
														className="ms-1"
														bg={menu.badgecolor ? menu.badgecolor : 'primary'}
													>
														{menu.badge}
													</Badge>
												) : (
													''
												)}
											</Link>
											{/* end of menu item without any childern items */}
										</Card>
									);
								}
							}
						})}
					</Accordion>
					{/* end of Dashboard Menu */}
					<div className="mt-auto">
						{currentSubscription && (
							<>
								{currentSubscription && currentSubscription.trial? (
									<>
									{(() => {
										const daysLeft = calculateDaysLeft(currentSubscription.trial.end);

										return daysLeft > 0 ? (
											<>
											<span className="ms-1 text-warning">
												<strong></strong>
											</span>
											{/* Gift Box */ }
											<Card className="bg-warning shadow-none text-center mx-4 my-4">
												<Card.Body className="py-4">
													<div className="">
														<h5 className="small">{daysLeft}{' '}{t('days left')}</h5>
														<h3 className="">{t('Upgrade your trial now')}</h3>
														<p className="fs-6">
															{t('After the free trial period, your plan will automatically switch to a free plan if you do not upgrade.')}
														</p>
														<Link to="/settings/general?tab=plans" className="btn btn-secondary btn-sm rounded mt-2">
															{t('Upgrade now')}
														</Link>
													</div>
												</Card.Body>
											</Card>
											</>
										) : (
											<span className="ms-1 text-dark">

											</span>
										);
									})()}
									</>
								) : (
									null
								)}	
								
								<Card bsPrefix="nav-item" key="subscription">
									{/* group title item */}
									<div className="navbar-heading">{t('Current Plan')}</div>
									{/* end of group title item */}
								</Card>
								<div className="mt-0 px-4 pb-3">
									<Link
										to="/settings/general?tab=plans"
										className="nav-link text-decoration-none"
										onClick={(e) =>
											isMobile ? props.onClick(!props.showMenu) : props.showMenu
										}
									>
										<OverlayTrigger
											key="right"
											placement="right"
											overlay={
												<Tooltip
													id={`tooltip-right`}
													className={`text-${getStatusColor(currentSubscription?.status)}`}
												>
													{currentSubscription?.status === 'active' && t('Active plan')}
													{currentSubscription?.status === 'trialing' && t('Trial period')}
													{currentSubscription?.status === 'canceled' && t('Canceled plan')}
													{currentSubscription?.status === 'incomplete_expired' && t('Payment required')}
												</Tooltip>
											}
										>
											<h5 className="mb-1 text-muted">
												{currentSubscription && currentSubscription.status ? (
													<>
														<DotBadge
															bg={
																currentSubscription.status === 'active'
																	? 'success'
																	: currentSubscription.status === 'trialing'
																		? 'warning'
																		: currentSubscription.status === 'canceled'
																			? 'info'
																			: currentSubscription.status === 'incomplete_expired'
																				? 'danger'
																				: 'info'
															}
														></DotBadge>

														{currentSubscription && currentSubscription.name}
													</>
												) : (
													''
												)}
							
											</h5>
										</OverlayTrigger>
									</Link>
								</div>
							</>
						)}
					</div>
				</div>
			</SimpleBar>
		</Fragment>
	);
};

export default NavbarVertical;
